/**
 * Playlist reads and the cross-domain playlist operations.
 *
 * The store holds playlists and applies single-domain mutations. This hook is where
 * the operations that touch more than one domain live, which is the division of
 * labour stated in hooks/index.ts:
 *
 *   - naming a playlist toasts (UI)
 *   - playing one starts the PLAYER on a queue
 *   - deleting a song has to leave every playlist (SONGS → PLAYLISTS)
 *
 * A playlist resolves its ids against the library on read (playlistSongs), so nothing
 * here synchronises anything: renaming or re-covering a song is already reflected
 * everywhere it appears. The one thing that cannot be handled on read is deletion,
 * because a deleted song's id would linger in the array — hence removeSongEverywhere,
 * called from useSongs.deleteSong, which is the single funnel every delete goes
 * through.
 *
 * NOT SIMULATED in the sense the Studio's AI tools are. A playlist does exactly what
 * it says: it holds the user's own songs in the order they chose, and playing one
 * really does move the player through them in that order. The only thing missing is
 * persistence, because playlistsApi is a mock — see its header.
 */
import { useMemo } from 'react';
import {
  playlistSongLibrary,
  playlistSongs,
  playlistSummary,
  playlistsWithSong,
  selectPlaylistById,
  usePlaylistsStore,
} from '../stores/playlistsStore';
import { usePlayerStore } from '../stores/playerStore';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { Playlist, Song } from '../types/domain';

export function usePlaylists() {
  // One field per selector: Zustand v5 compares with Object.is, so an object literal
  // here would return a new reference on every unrelated store change.
  const playlists = usePlaylistsStore((s) => s.playlists);
  const playlistSongsOnly = usePlaylistsStore((s) => s.playlistSongs);
  const createPlaylistRaw = usePlaylistsStore((s) => s.createPlaylist);
  const rememberPlaylistSong = usePlaylistsStore((s) => s.rememberPlaylistSong);
  const renamePlaylistRaw = usePlaylistsStore((s) => s.renamePlaylist);
  const deletePlaylistRaw = usePlaylistsStore((s) => s.deletePlaylist);
  const addSongRaw = usePlaylistsStore((s) => s.addSongToPlaylist);
  const removeSongRaw = usePlaylistsStore((s) => s.removeSongFromPlaylist);
  const moveSongRaw = usePlaylistsStore((s) => s.moveSongInPlaylist);

  const songs = useSongsStore((s) => s.songs);
  const playQueue = usePlayerStore((s) => s.playQueue);
  const dropFromQueue = usePlayerStore((s) => s.dropFromQueue);
  const showToast = useUiStore((s) => s.showToast);

  const resolvableSongs = useMemo(
    () => playlistSongLibrary(songs, playlistSongsOnly),
    [songs, playlistSongsOnly]
  );

  /*
    Sorted newest-first, as the library is. Memoised on the array rather than sorted
    in the store, because sorting on write would make the stored order a second
    ordering to keep in step with `seq` — and `seq` is the one that means something.
  */
  const sortedPlaylists = useMemo(() => [...playlists].sort((a, b) => b.seq - a.seq), [playlists]);

  return {
    playlists: sortedPlaylists,

    /** Looks up a playlist by id, for route params. */
    getPlaylist(id: string | undefined): Playlist | null {
      return selectPlaylistById(playlists, id ?? null);
    },

    /** The playlist's songs, in playlist order. Unresolvable ids are dropped. */
    songsIn(playlist: Playlist): Song[] {
      return playlistSongs(playlist, resolvableSongs);
    },

    /** "3 tracks · 9:45". */
    summaryFor(playlist: Playlist): string {
      return playlistSummary(playlist, resolvableSongs);
    },

    /** Which playlists already contain a song. Drives the "Add to playlist" ticks. */
    playlistsWith(songId: string): Playlist[] {
      return playlistsWithSong(playlists, songId);
    },

    /**
     * Creates a playlist, optionally seeded with a song.
     *
     * Returns the playlist so a caller can navigate straight to it. An empty or
     * whitespace name is refused rather than saved: a playlist called "" is
     * unnameable afterwards, since the rename dialog would open on a blank field
     * with nothing to distinguish it from its neighbours in the list.
     */
    createPlaylist(name: string, songId?: string, seedSong?: Song): Playlist | null {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const playlist = createPlaylistRaw(trimmed, songId ? [songId] : []);
      if (songId) {
        const song = seedSong ?? resolvableSongs.find((candidate) => candidate.id === songId);
        if (song) rememberPlaylistSong(song);
      }
      showToast(songId ? `Added to new playlist "${trimmed}"` : `Playlist "${trimmed}" created`);
      return playlist;
    },

    /** Renames a playlist. An emptied name is refused, as on create. */
    renamePlaylist(playlist: Playlist, name: string) {
      const trimmed = name.trim();
      if (!trimmed || trimmed === playlist.name) return;
      renamePlaylistRaw(playlist.id, trimmed);
      showToast(`Renamed to "${trimmed}"`);
    },

    /**
     * Deletes a playlist.
     *
     * The songs are untouched — a playlist is a view of the library, not a container
     * that owns what is in it. Said in the toast because "delete" on a screen full of
     * tracks is worth being unambiguous about.
     */
    deletePlaylist(playlist: Playlist) {
      deletePlaylistRaw(playlist.id);
      showToast(`Playlist "${playlist.name}" deleted — your songs are still in your library`);
    },

    /** Adds a song, toasting either way so a duplicate press is not silent. */
    addSong(playlist: Playlist, song: Song) {
      if (playlist.songIds.includes(song.id)) {
        showToast(`"${song.title}" is already in "${playlist.name}"`);
        return;
      }
      rememberPlaylistSong(song);
      addSongRaw(playlist.id, song.id);
      showToast(`Added "${song.title}" to "${playlist.name}"`);
    },

    /**
     * Removes a song from a playlist.
     *
     * Also drops it from the player's queue, so a track removed while the playlist is
     * playing does not stay in the play order — the queue is a snapshot of the order
     * playback started in, and this is the one edit that has to reach into it. What is
     * playing right now is deliberately left alone: stopping the music because a
     * later track was removed would be a surprising thing for an edit to do.
     */
    removeSong(playlist: Playlist, song: Song) {
      removeSongRaw(playlist.id, song.id);
      dropFromQueue(song.id);
      showToast(`Removed "${song.title}" from "${playlist.name}"`);
    },

    /** Moves a song one place earlier (-1) or later (+1). */
    moveSong(playlist: Playlist, song: Song, direction: -1 | 1) {
      moveSongRaw(playlist.id, song.id, direction);
    },

    /**
     * Plays a playlist from `startIndex`.
     *
     * Queues the RESOLVED songs, not the raw ids, so a playlist holding a stale id
     * cannot put a song that does not exist into the play order — the clock would
     * find no song for it and stall. Refuses an empty playlist rather than starting
     * silent playback with nothing to show in the transport.
     */
    playPlaylist(playlist: Playlist, startIndex = 0) {
      const tracks = playlistSongs(playlist, resolvableSongs);
      if (tracks.length === 0) {
        showToast(`"${playlist.name}" is empty — add songs to play it`);
        return;
      }
      playQueue(
        tracks.map((s) => s.id),
        Math.max(0, Math.min(tracks.length - 1, startIndex))
      );
    },
  };
}
